import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '@ohif/ui-next';

interface FilterDropdownProps {
  label: string;
  options: { value: string; label: string }[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : label;

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange(optionValue === value ? '' : optionValue);
    }
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          height: '32px',
          padding: '0 8px',
          backgroundColor: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 500,
          color: '#cbd5e1',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = '#334155';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = '#1e293b';
        }}
      >
        <span>{displayLabel}</span>
        <Icons.DownArrowIcon
          width="16"
          height="16"
          style={{
            color: '#cbd5e1',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '4px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            minWidth: '150px',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                textAlign: 'left',
                fontSize: '14px',
                color: option.value === value ? '#60a5fa' : '#cbd5e1',
                backgroundColor: option.value === value ? '#1e3a8a' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={e => {
                if (option.value !== value) {
                  e.currentTarget.style.backgroundColor = '#334155';
                }
              }}
              onMouseLeave={e => {
                if (option.value !== value) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
